import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { ChatThread } from '../types/agui';

export function useChatHistory(userId: string | undefined) {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to the user's threads sorted by updatedAt desc
  useEffect(() => {
    if (!userId) {
      setThreads([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const threadsRef = collection(db, 'users', userId, 'threads');
    const threadsQuery = query(threadsRef, orderBy('updatedAt', 'desc'));

    const unsubscribe = onSnapshot(
      threadsQuery,
      (snapshot) => {
        const fetchedThreads: ChatThread[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            title: data.title || 'Conversation',
            agentId: data.agentId || 'finance_advisor_v14',
            userId: data.userId || userId,
            createdAt: data.createdAt || new Date().toISOString(),
            updatedAt: data.updatedAt || new Date().toISOString(),
            lastMessage: data.lastMessage || '',
            messageCount: data.messageCount || 0,
          };
        });
        setThreads(fetchedThreads);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching chat threads:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Delete a thread along with all its nested messages in a batch
  const deleteThread = useCallback(
    async (threadId: string) => {
      if (!userId || !threadId) return;

      try {
        const batch = writeBatch(db);

        // 1. Delete all message documents under the thread
        const messagesRef = collection(db, 'users', userId, 'threads', threadId, 'messages');
        const messagesSnapshot = await getDocs(messagesRef);
        messagesSnapshot.forEach((msgDoc) => {
          batch.delete(msgDoc.ref);
        });

        // 2. Delete the thread root document
        const threadDocRef = doc(db, 'users', userId, 'threads', threadId);
        batch.delete(threadDocRef);

        await batch.commit();
      } catch (err: any) {
        console.error('Failed to delete thread:', err);
        throw err;
      }
    },
    [userId]
  );

  return {
    threads,
    loading,
    error,
    deleteThread,
  };
}
